import React, { useState } from "react";

// @mui material components
import {
  Card,
  Grid,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Collapse,
  TextField,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";

function Botons() {
  // Estados para diálogos
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogAction, setDialogAction] = useState(null);

  // Estado para carga de archivos al rechazar
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  // Estado para controlar si se ha iniciado el proceso de rechazo
  const [rejectionStarted, setRejectionStarted] = useState(false);

  // --- Manejo de diálogos ---
  const handleOpenDialog = (title, message, action) => {
    setDialogTitle(title);
    setDialogMessage(message);
    setDialogAction(() => action);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDialogAction(null);
  };

  const handleConfirmDialog = () => {
    if (dialogAction) dialogAction();
    handleCloseDialog();
  };

  // --- Acciones principales ---
  const handleAccept = () => {
    handleOpenDialog(
      "Confirmar Aceptación",
      "¿Está seguro de que desea aceptar la conciliación? Esta acción no se puede deshacer.",
      () => {
        // Resetear estado de rechazo si se acepta
        setRejectionStarted(false);
        setShowFileUpload(false);
      }
    );
  };

  const handleReject = () => {
    setShowFileUpload(true);
    setRejectionStarted(true);
  };

  const handleManualSend = () => {
    handleOpenDialog(
      "Confirmar Envío Manual",
      "¿Está seguro de que desea iniciar el envío manual de correos?"
    );
  };

  // --- Manejo del rechazo ---
  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const handleSubmitRejection = () => {
    if (!selectedFile || !rejectionReason) {
      handleOpenDialog(
        "Datos Incompletos",
        "Por favor, adjunte un archivo y proporcione el motivo del rechazo.",
        null
      );
      return;
    }

    handleOpenDialog(
      "Confirmar Rechazo",
      "¿Está seguro de que desea rechazar la conciliación con el archivo adjunto?",
      () => {
        setShowFileUpload(false);
        setSelectedFile(null);
        setRejectionReason("");
        // Mantener el estado de rechazo iniciado
      }
    );
  };

  const handleCancelRejection = () => {
    handleOpenDialog(
      "Cancelar Rechazo",
      "¿Está seguro de que desea cancelar el proceso de rechazo? Los datos ingresados se perderán.",
      () => {
        setShowFileUpload(false);
        setSelectedFile(null);
        setRejectionReason("");
        setRejectionStarted(false); // Resetear estado de rechazo
      }
    );
  };

  return (
    <>
      <Card sx={{ height: showFileUpload ? "auto" : "25%" }}>
        {/* Encabezado */}
        <MDBox
          mx={1}
          mt={1}
          py={1}
          px={1}
          variant="gradient"
          bgColor="success"
          borderRadius="lg"
          coloredShadow="info"
        >
          <MDTypography variant="h6" color="white">
            Acciones de Conciliación:
          </MDTypography>
        </MDBox>

        <MDBox p={3}>
          <Grid container spacing={3}>
            {/* Sección: Aceptar/Rechazar */}
            <Grid item xs={12} md={6}>
              <MDBox
                display="flex"
                flexDirection="column"
                alignItems="center"
                p={2}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  height: "100%",
                }}
              >
                <MDTypography variant="h6" fontWeight="medium" gutterBottom>
                  Estado de Conciliación
                </MDTypography>

                <MDTypography variant="body2" color="text" textAlign="center" mb={2}>
                  Selecciona una acción para la conciliación actual
                </MDTypography>

                <MDBox display="flex" gap={2} mt={1}>
                  <MDButton
                    variant="contained"
                    color="success"
                    onClick={handleAccept}
                    disabled={rejectionStarted} // Deshabilitado si se inició rechazo
                    sx={{
                      borderRadius: 1,
                      textTransform: "none",
                      fontWeight: "medium",
                      opacity: rejectionStarted ? 0.6 : 1,
                    }}
                  >
                    Aceptar Conciliación
                  </MDButton>

                  <MDButton
                    variant="contained"
                    color="error"
                    onClick={handleReject}
                    disabled={rejectionStarted}
                    sx={{
                      borderRadius: 1,
                      textTransform: "none",
                      fontWeight: "medium",
                      opacity: rejectionStarted ? 0.6 : 1,
                    }}
                  >
                    Rechazar Conciliación
                  </MDButton>
                </MDBox>

                {rejectionStarted && (
                  <MDTypography variant="caption" color="error" textAlign="center" mt={1}>
                    * El proceso de rechazo ha sido iniciado. No puede aceptar la conciliación.
                  </MDTypography>
                )}
              </MDBox>
            </Grid>

            {/* Sección: Envío Manual */}
            <Grid item xs={12} md={6}>
              <MDBox
                display="flex"
                flexDirection="column"
                alignItems="center"
                p={2}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  height: "100%",
                }}
              >
                <MDTypography variant="h6" fontWeight="medium" gutterBottom>
                  Envío Manual
                </MDTypography>

                <MDTypography variant="body2" color="text" textAlign="center" mb={2}>
                  Realiza el envío manual de correos para conciliación
                </MDTypography>

                <MDButton
                  variant="outlined"
                  color="info"
                  onClick={handleManualSend}
                  sx={{
                    borderRadius: 1,
                    textTransform: "none",
                    fontWeight: "medium",
                    mt: 1,
                  }}
                >
                  Iniciar Envío Manual
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>

          {/* Área: Rechazo con archivo */}
          <Collapse in={showFileUpload}>
            <MDBox
              mt={3}
              p={3}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                backgroundColor: "#f9f9f9",
              }}
            >
              <MDTypography variant="h6" fontWeight="medium" gutterBottom>
                Documentación de Rechazo
              </MDTypography>

              <MDTypography variant="body2" color="text" mb={2}>
                Por favor, adjunte un archivo que justifique el rechazo y proporcione el motivo.
              </MDTypography>

              <Grid container spacing={2}>
                {/* Motivo */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Motivo del rechazo"
                    multiline
                    rows={3}
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    variant="outlined"
                  />
                </Grid>

                {/* Archivo */}
                <Grid item xs={12}>
                  <MDButton
                    variant="outlined"
                    component="label"
                    color="info"
                    sx={{ mr: 2, textTransform: "none" }}
                  >
                    Seleccionar Archivo
                    <input type="file" hidden onChange={handleFileChange} />
                  </MDButton>

                  {selectedFile && (
                    <MDTypography variant="caption" color="text">
                      Archivo seleccionado: {selectedFile.name}
                    </MDTypography>
                  )}
                </Grid>
                <Grid item xs={12} lg={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} lg={6}>
                      <MDBox display="flex" justifyContent="flex-end">
                        <MDButton
                          variant="contained"
                          color="error"
                          onClick={handleSubmitRejection}
                          sx={{ textTransform: "none" }}
                          fullWidth
                        >
                          Enviar Rechazo
                        </MDButton>
                      </MDBox>
                    </Grid>

                    <Grid item xs={12} sm={6} lg={6}>
                      <MDBox display="flex" justifyContent="flex-start">
                        <MDButton
                          variant="outlined"
                          color="secondary"
                          onClick={handleCancelRejection}
                          sx={{ textTransform: "none" }}
                          fullWidth
                        >
                          Cancelar
                        </MDButton>
                      </MDBox>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </MDBox>
          </Collapse>

          <Divider sx={{ my: 2 }} />
        </MDBox>
      </Card>

      {/* Diálogo de confirmación */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{dialogTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">{dialogMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseDialog} color="error">
            Cancelar
          </MDButton>
          <MDButton onClick={handleConfirmDialog} color="success" autoFocus>
            Aceptar
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Botons;
